import { CheckCircle, Upload } from 'lucide-react';
import React, { useState } from 'react';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { DocumentSelectionModal } from '@/components/tasks/task-detail/DocumentSelectionModal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';

interface PhotoIdUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (blobId: string) => void;
  onClear?: () => void;
  matterId: string;
  isUploading?: boolean;
  existingBlobId?: string | null;
}

export const PhotoIdUploadModal: React.FC<PhotoIdUploadModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  onClear,
  matterId,
  isUploading = false,
  existingBlobId,
}) => {
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // Upload manager for new files
  const { handleUploadToCollection } = useUploadManagerUnified({
    matterId,
    refetch: () => {},
  });

  const handleClose = () => {
    setShowDocumentModal(false);
    onClose();
  };

  const handleSelectFromDocuments = () => {
    setShowDocumentModal(true);
  };

  const handleDocumentSelect = async (blobIds: string[]) => {
    if (blobIds.length > 0) {
      await onSelect(blobIds[0]);
    }
    setShowDocumentModal(false);
  };

  return (
    <>
      <Dialog
        open={isOpen && !showDocumentModal}
        onClose={handleClose}
        size="3xl"
        nested
        zIndex={Z_INDEX_MODAL_NESTED}
      >
        <DialogTitle>Upload Photo ID</DialogTitle>

        <DialogBody>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Guidelines Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Photo ID Guidelines
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs">
                      Government-issued ID (driver&apos;s license, passport,
                      state ID)
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs">Clear and readable text</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs">Full ID visible in frame</span>
                  </div>
                </div>
              </div>

              {/* Example Images */}
              <div>
                <h4 className="text-xs font-medium text-gray-900 mb-3">
                  Examples
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center">
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3 mb-2">
                      <div className="bg-white rounded p-2">
                        <div className="text-xs text-gray-800 space-y-1">
                          <div className="font-medium">DRIVER LICENSE</div>
                          <div className="text-gray-600">John A. Smith</div>
                          <div className="text-gray-500">DOB: 01/15/1985</div>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-green-700 font-medium">
                      ✓ Good
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Clear, complete, readable
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 mb-2">
                      <div className="bg-gray-200 rounded p-2">
                        <div className="text-xs text-gray-400 space-y-1 blur-sm">
                          <div className="font-medium">DRIVER LICENSE</div>
                          <div>John A. Smith</div>
                          <div>DOB: 01/15/1985</div>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-red-700 font-medium">
                      ✗ Poor
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Blurry, hard to read
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Tip:</strong> Use good lighting and ensure all text on
                  the ID is clearly visible and readable.
                </p>
              </div>
            </div>

            {/* Upload Section */}
            <div className="space-y-4">
              <div className="space-y-4">
                {/* Select from existing documents */}
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <Upload className="mx-auto h-8 w-8 text-gray-400 mb-3" />
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Select or Upload Photo ID
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    Choose from existing documents or upload a new one
                  </p>
                  <Button
                    onClick={handleSelectFromDocuments}
                    disabled={isUploading}
                    className="gap-2"
                  >
                    <Upload className="h-3 w-3" />
                    Select or Upload ID
                  </Button>
                </div>

                <div className="text-xs text-gray-500 text-center">
                  Supports JPEG, PNG, PDF formats
                </div>

                {/* Clear button for existing ID */}
                {onClear && existingBlobId && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={() => {
                        onClear();
                        handleClose();
                      }}
                      variant="outline"
                      disabled={isUploading}
                      className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                    >
                      Clear Photo ID
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* Document Selection Modal */}
      {showDocumentModal && (
        <DocumentSelectionModal
          open
          onClose={() => setShowDocumentModal(false)}
          singleFile
          matterId={matterId}
          onSelectDocuments={handleDocumentSelect}
          onUpload={handleUploadToCollection}
          initialSelectedBlobIds={existingBlobId ? [existingBlobId] : []}
        />
      )}
    </>
  );
};
